import type { Model } from '@ricoh/ria.client.core/model';
import type { QueryRequestData } from '@ricoh/ria.client.core/plugins/swr/request';
import type { DataSource } from '@ricoh/ria.client.core/types/component/data.type';
import type { ValueOf } from 'type-fest';

import { DATETIME_UNIT } from '@ricoh/ria.client.core/constants/core/unit';
import { ClientError } from '@ricoh/ria.client.core/errors';
import { getData, getHeaders, getPageableData } from '@ricoh/ria.client.core/plugins/swr/common';
import { createFetcher } from '@ricoh/ria.client.core/plugins/swr/fetcher';
import { newKey } from '@ricoh/ria.client.core/utils/data/key';
import { omit } from '@ricoh/ria.client.core/utils/data/object';
import { base64 } from '@ricoh/ria.client.core/utils/encrypt/base64';
import { isAxiosError } from 'axios';
import { clearIntervalAsync, setIntervalAsync } from 'set-interval-async';

import type {
  Batch,
  BatchFieldWithValue,
  BatchView,
  DefaultBatchPreferences,
  Document,
  DocumentFieldWithValue,
  DraftBatch,
  OCRPosition,
  ProfileSettings,
} from '~/common/model/api/batch';
import type { BatchTypeValueSet, DocumentTypeValueSet } from '~/common/model/api/batch-type';
import type { DocumentLayout } from '~/common/model/api/batch-type/document-layout';
import type { ProcessBatch, TransformDocument, TransformDocuments, TransformFieldValues } from '~/services/server/api/batch/mapper';

import { prepareBatchOptions, prepareDocumentOptions } from '~/services/server/api/batch-type/mapper';
import { prepareBatchFields, prepareBatchStage, prepareBatchStatus, prepareDocuments, prepareProcessBatchType } from '~/services/server/api/batch/mapper';
import { prepareScanningOptions } from '~/services/server/api/scanning-profile/mapper';
import { prepareClassificationOptions, prepareSeparationOptions } from '~/services/server/api/separation-profile/mapper';
import axiosPlugin from '~/plugins/axios';

import type {
  AddSourceOfflineResponse,
  AddSourceResponse,
  BatchInfoResponse,
  GetAssignedBatchTypeValueSetResponse,
  GetBatchFieldsDataSourceResponse,
  GetBatchFieldsResponse,
  GetBatchResponse,
  GetBatchViewResponse,
  GetDocumentFieldsDataSourceResponse,
  GetDocumentFieldsResponse,
  GetDraftsResponse,
  GetProfilesResponse,
  GetSourceOfflineResponse,
  ValidateFieldsResponse,
} from './_fetcher.type';
import type {
  AddSourceOfflineRequestData,
  AddSourceOnlineManualRequestData,
  AddSourceOnlineRequestData,
  CheckSourceOnlineRequestData,
  CreateBatchRequestData,
  GetBatchFieldDataSourceRequestData,
  GetBatchRequestData,
  GetDataSourceRequestData,
  GetDocumentFieldDataSourceRequestData,
  GetDocumentFieldsRequestData,
  GetDocumentLayoutRequestData,
  GetOCRPageRequestData,
  GetSourceOfflineRequestData,
  GetSourceOnlineRequestData,
  MergeBatchRequestData,
  RemoveBatchesRequestData,
  SaveBatchRequestData,
  SaveProfilesRequestData,
  SubmitBatchRequestData,
  ValidateFieldsRequestData,
} from './_request.type';

import { saveBatchPrepareError } from './_error';
import requestConfig from './_request';

const { options, endpoints, createError } = requestConfig;

const client = axiosPlugin.createClient(options);

const SOURCE_CHECKING_INTERVAL = 5 * DATETIME_UNIT.SECOND;
const SOURCE_CHECKING_TIMEOUT = 30 * DATETIME_UNIT.MINUTE;
const SOURCE_CHECKING_MAX_TIMES = SOURCE_CHECKING_TIMEOUT / SOURCE_CHECKING_INTERVAL;

/**
 * Get Documents with loop source checking.
 *
 * @param {CheckSourceOnlineRequestData} requestData RequestData.
 *
 * @returns {TransformDocument[]} Documents.
 */
async function getSourceOnlinePolling({ id, fileKey, signal }: CheckSourceOnlineRequestData): Promise<TransformDocument[]> {
  return new Promise<TransformDocument[]>((resolve, reject) => {
    const createAbortedError = () =>
      new ClientError({
        module: 'service',
        message: 'Checking source was aborted',
        cause: { id, fileKey },
      });

    if (signal?.aborted) {
      reject(createAbortedError());
      return;
    }

    let loopTimes = 0;
    const checkingProcess = setIntervalAsync(async () => {
      if (signal?.aborted) {
        return;
      }

      loopTimes += 1;
      if (loopTimes > SOURCE_CHECKING_MAX_TIMES) {
        reject(
          new ClientError({
            module: 'service',
            message: 'Timeout to check source',
            cause: { id, fileKey },
          }),
        );
        await clearIntervalAsync(checkingProcess);
        return;
      }

      try {
        const response = await client.request(endpoints.getSourceOnline(id, fileKey));
        // * Aborted while the request was in flight -> already rejected, ignore the result
        if (signal?.aborted || !response.data) {
          return;
        }

        const data = getData<TransformDocuments>(response);
        resolve(Object.values(data));
        await clearIntervalAsync(checkingProcess);
      } catch (exception) {
        if (signal?.aborted) {
          return;
        }

        reject(createError('getSourceOnline', 'Failed to get source.', exception));
        await clearIntervalAsync(checkingProcess);
      }
    }, SOURCE_CHECKING_INTERVAL);

    signal?.addEventListener(
      'abort',
      async () => {
        reject(createAbortedError());
        await clearIntervalAsync(checkingProcess);
      },
      { once: true },
    );
  });
}

/**
 * Check whether the exception is a 404 Not Found response from the server.
 *
 * @param {unknown} exception Exception.
 *
 * @returns {boolean} Is 404 Not Found or not.
 */
function isNotFound(exception: unknown): boolean {
  return isAxiosError(exception) && exception.response?.status === 404;
}

/**
 * Clean source online of a batch.
 *
 * @param {string} id Batch id.
 *
 * @returns {Promise<boolean>} `false` if the batch no longer exists, so there was nothing to clean.
 */
async function cleanSourceOnline(id: string): Promise<boolean> {
  try {
    await client.request(endpoints.cleanSourceOnline(id));
    return true;
  } catch (exception) {
    // * The batch is already gone (e.g. deleted while the user was discarding it) -> nothing to clean
    if (isNotFound(exception)) {
      return false;
    }

    throw createError('cleanSourceOnline', 'Failed to clean source.', exception);
  }
}

/** GetAssignedBatchTypeValueSet fetcher. */
export const getAssignedBatchTypeValueSetFetcher = createFetcher<void, BatchTypeValueSet[]>(endpoints.getAssignedBatchTypeValueSet.key, async () => {
  try {
    const data = getData<GetAssignedBatchTypeValueSetResponse>(await client.request(endpoints.getAssignedBatchTypeValueSet()));

    return Object.entries(data).map<BatchTypeValueSet>(([batchTypeId, { documents, ...batchType }]) => ({
      id: batchTypeId,
      ...batchType,
      defaultPriority: batchType.defaultPriority ?? 'normal',
      documents: documents
        ? Object.entries(documents).map<DocumentTypeValueSet>(([documentTypeId, documentType]) => ({
            id: documentTypeId,
            ...documentType,
          }))
        : [],
    }));
  } catch (exception) {
    throw createError('getAssignedBatchTypeValueSet', 'Failed to get assigned batch type value set.', exception);
  }
});

/** GetDefaultBatchPreferences fetcher. */
export const getDefaultBatchPreferencesFetcher = createFetcher<void, DefaultBatchPreferences>(endpoints.getDefaultBatchPreferences.key, async () => {
  try {
    return getData<DefaultBatchPreferences>(await client.request(endpoints.getDefaultBatchPreferences()));
  } catch (exception) {
    throw createError('getDefaultBatchPreferences', 'Failed to get default batch preferences.', exception);
  }
});

/** GetDraftList fetcher. */
export const getDraftListFetcher = createFetcher<QueryRequestData, DataSource<DraftBatch>>(endpoints.getDraftList.key, async (query) => {
  try {
    const { page, total, items } = getPageableData<GetDraftsResponse>(query, await client.request(endpoints.getDraftList(query)));

    return {
      items: items.map<DraftBatch>(({ type, ...draftBatch }) => ({
        ...draftBatch,
        type: type.name,
        documentCount: draftBatch.documentCount,
      })),
      total,
      page,
      sort: query.sort,
      filter: query.filter ?? undefined,
    };
  } catch (exception) {
    throw createError('getDraftList', 'Failed to get draft batch list.', exception);
  }
});

/** GetBatchFields fetcher. */
export const getBatchFieldsFetcher = createFetcher<string, Model<ValueOf<GetBatchFieldsResponse>>[]>(endpoints.getBatchFields.key, async (batchId) => {
  try {
    const data = getData<GetBatchFieldsResponse>(await client.request(endpoints.getBatchFields(batchId)));

    return Object.entries(data).map<Model<ValueOf<GetBatchFieldsResponse>>>(([fieldId, field]) => ({
      ...field,
      id: fieldId,
    }));
  } catch (exception) {
    throw createError('getBatchFields', 'Failed to get batch fields.', exception);
  }
});

/** GetDocumentFields fetcher. */
export const getDocumentFieldsFetcher = createFetcher<GetDocumentFieldsRequestData, Model<ValueOf<GetDocumentFieldsResponse>>[]>(
  endpoints.getDocumentFields.key,
  async (requestData) => {
    try {
      const data = getData<GetDocumentFieldsResponse>(await client.request(endpoints.getDocumentFields(requestData)));

      return Object.entries(data).map<Model<ValueOf<GetDocumentFieldsResponse>>>(([fieldId, field]) => ({
        ...field,
        id: fieldId,
      }));
    } catch (exception) {
      throw createError('getDocumentFields', 'Failed to get document fields.', exception);
    }
  },
);

/** AccessBatch fetcher. */
export const accessBatchFetcher = createFetcher<GetBatchRequestData, Batch>(endpoints.accessBatch.key, async ({ id, stage }) => {
  try {
    const {
      id: batchId,
      type,
      status,
      fields,
      documents,
      documentOptions,
      ...batchInfo
    } = getData<GetBatchResponse>(await client.request(endpoints.accessBatch(id)));

    const batchStatus = prepareBatchStatus(status, stage);

    const batch: Batch = {
      ...batchInfo,
      id: batchId,
      status: batchStatus,
      stage: prepareBatchStage(batchStatus),
      type: prepareProcessBatchType(type),
      documents: [],
      fields: [],
      batchOptions: [],
      documentOptions: prepareDocumentOptions(documentOptions),
    };

    batch.fields = await prepareBatchFields(batch, getBatchFieldsFetcher, fields ?? {});
    batch.documents = await prepareDocuments(batch, documents ?? {}, getDocumentFieldsFetcher);

    return batch;
  } catch (exception) {
    throw createError('accessBatch', 'Failed to access batch.', exception);
  }
});

/** GetBatchView fetcher. */
export const getBatchViewFetcher = createFetcher<string, BatchView>(endpoints.getBatchView.key, async (batchId) => {
  try {
    const { type, documents, ...batchInfo } = getData<GetBatchViewResponse>(await client.request(endpoints.getBatchView(batchId)));

    const batchStatus = prepareBatchStatus(batchInfo.status);

    const batch: BatchView = {
      ...omit(batchInfo, 'fields', 'documentOptions'),
      status: batchStatus,
      stage: prepareBatchStage(batchStatus),
      type: prepareProcessBatchType(type),
      documents: [],
    };

    batch.documents = await prepareDocuments(batch, documents ?? {});

    return batch;
  } catch (exception) {
    throw createError('getBatchView', 'Failed to get batch view.', exception);
  }
});

/** CreateBatch fetcher. */
export const createBatchFetcher = createFetcher<CreateBatchRequestData, BatchInfoResponse>(endpoints.createBatch.key, async (requestData) => {
  try {
    const response = await client.request(endpoints.createBatch(requestData));
    const { location } = getHeaders(response, 'Location');
    if (!location) {
      throw new ClientError({
        module: 'service',
        message: 'Not found location of batch',
        cause: response,
      });
    }

    const batch = getData<BatchInfoResponse>(await client.get(location));
    return {
      ...batch,
      status: prepareBatchStatus(batch.status),
    };
  } catch (exception) {
    throw createError('createBatch', 'Failed to create batch.', exception);
  }
});

/** SaveBatch fetcher. */
export const saveBatchFetcher = createFetcher<SaveBatchRequestData>(endpoints.saveBatch.key, async (requestData) => {
  try {
    await client.request(endpoints.saveBatch(requestData));
  } catch (exception) {
    throw createError('saveBatch', 'Failed to save batch.', exception, saveBatchPrepareError(requestData));
  }
});

/** MergeBatch fetcher. */
export const mergeBatchFetcher = createFetcher<MergeBatchRequestData, ProcessBatch['documents']>(endpoints.mergeBatch.key, async (requestData) => {
  try {
    await client.request(endpoints.mergeBatch(requestData));
    const { documents } = getData<GetBatchResponse>(await client.request(endpoints.getBatchView(requestData.id)));
    const responseDocumentIds = documents ? Object.keys(documents) : [];

    const requestDocuments = Object.values(requestData.documents);
    if (responseDocumentIds.length !== requestDocuments.length) {
      throw new ClientError({
        module: 'service',
        message: 'Count of documents after merging not match',
        cause: {
          request: requestData.documents,
          response: documents,
        },
      });
    }

    return requestDocuments.reduce<ProcessBatch['documents']>((prev, requestDocument, index) => {
      const responseDocumentId = responseDocumentIds[index];
      if (responseDocumentId) {
        prev[responseDocumentId] = requestDocument;
      }

      return prev;
    }, {});
  } catch (exception) {
    throw createError('mergeBatch', 'Failed to merge batch.', exception);
  }
});

/** CheckSourceOnline fetcher. */
export const checkSourceOnlineFetcher = createFetcher<CheckSourceOnlineRequestData, TransformDocument[]>(endpoints.getSourceOnline.key, getSourceOnlinePolling);

/** GetSourceOnline fetcher. */
export const getSourceOnlineFetcher = createFetcher<GetSourceOnlineRequestData, Document[]>(endpoints.getSourceOnline.key, async ({ batch, fileKey }) => {
  try {
    const data = getData<TransformDocuments>(await client.request(endpoints.getSourceOnline(batch.id, fileKey)));
    const documents = Object.values(data).reduce<TransformDocuments>((prev, document, index) => {
      prev[newKey()] = {
        ...document,
        sourceRef: {
          key: fileKey,
          index,
        },
      };

      return prev;
    }, {});

    return await prepareDocuments(batch, documents, getDocumentFieldsFetcher);
  } catch (exception) {
    throw createError('getSourceOnline', 'Failed to get source.', exception);
  }
});

/** AddSourceOnline fetcher. */
export const addSourceOnlineFetcher = createFetcher<AddSourceOnlineRequestData, AddSourceResponse>(endpoints.addSourceOnline.key, async (requestData) => {
  try {
    return getData<AddSourceResponse>(await client.request(endpoints.addSourceOnline(requestData)));
  } catch (exception) {
    throw createError('addSourceOnline', 'Failed to add source.', exception);
  }
});

/** AddSourceOnline manual fetcher. */
export const addSourceOnlineManualFetcher = createFetcher<AddSourceOnlineManualRequestData, Document[]>(
  endpoints.addSourceOnlineManual.key,
  async ({ batch, sourceType, files }) => {
    try {
      const sources = getData<AddSourceResponse>(
        await client.request(
          endpoints.addSourceOnlineManual({
            id: batch.id,
            sourceType,
            files,
          }),
        ),
      );

      const documentsFromSources = await Promise.all(
        Object.values(sources).map(async ({ fileKey }) =>
          getSourceOnlinePolling({
            id: batch.id,
            fileKey,
          }),
        ),
      );

      const documents = documentsFromSources.flat().reduce<TransformDocuments>((prev, document) => {
        prev[newKey()] = document;

        return prev;
      }, {});

      return await prepareDocuments(batch, documents, getDocumentFieldsFetcher);
    } catch (exception) {
      throw createError('addSourceOnlineManual', 'Failed to add source manually.', exception);
    }
  },
);

/** CleanSourceOnline fetcher. */
export const cleanSourceOnlineFetcher = createFetcher<string>(endpoints.cleanSourceOnline.key, async (id) => {
  await cleanSourceOnline(id);
});

/** GetSourceOffline fetcher. */
export const getSourceOfflineFetcher = createFetcher<GetSourceOfflineRequestData, GetSourceOfflineResponse>(
  endpoints.getSourceOffline.key,
  async ({ id, fileKey }) =>
    new Promise<GetSourceOfflineResponse>((resolve, reject) => {
      let loopTimes = 0;
      const checkingProcess = setIntervalAsync(async () => {
        loopTimes += 1;
        if (loopTimes > SOURCE_CHECKING_MAX_TIMES) {
          reject(
            new ClientError({
              module: 'service',
              message: 'Timeout to check source offline',
              cause: { id, fileKey },
            }),
          );
          await clearIntervalAsync(checkingProcess);
          return;
        }

        try {
          const response = await client.request(endpoints.getSourceOffline(id, fileKey));
          if (!response.data) {
            return;
          }

          const data = getData<GetSourceOfflineResponse>(response);
          resolve(data);
          await clearIntervalAsync(checkingProcess);
        } catch (exception) {
          reject(createError('getSourceOffline', 'Failed to get source offline.', exception));
          await clearIntervalAsync(checkingProcess);
        }
      }, SOURCE_CHECKING_INTERVAL);
    }),
);

/** AddSourceOffline fetcher. */
export const addSourceOfflineFetcher = createFetcher<AddSourceOfflineRequestData, AddSourceOfflineResponse>(
  endpoints.addSourceOffline.key,
  async (requestData) => {
    try {
      return getData<AddSourceOfflineResponse>(await client.request(endpoints.addSourceOffline(requestData)));
    } catch (exception) {
      throw createError('addSourceOffline', 'Failed to add source offline.', exception);
    }
  },
);

/** DeleteBatch fetcher. */
export const deleteBatchFetcher = createFetcher<string>(endpoints.deleteBatch.key, async (id) => {
  try {
    // * Batch no longer exists -> already deleted, nothing more to do
    if (!(await cleanSourceOnline(id))) {
      return;
    }

    await client.request(endpoints.deleteBatch(id));
  } catch (exception) {
    throw createError('deleteBatch', 'Failed to delete batch.', exception);
  }
});

/** Remove Batches fetcher. */
export const removeBatchesFetcher = createFetcher<RemoveBatchesRequestData>(endpoints.removeBatches.key, async (requestData) => {
  try {
    await client.request(endpoints.removeBatches(requestData));
  } catch (exception) {
    throw createError('removeBatches', 'Failed to remove batches.', exception);
  }
});

/** SubmitBatch fetcher. */
export const submitBatchFetcher = createFetcher<SubmitBatchRequestData>(endpoints.submitBatch.key, async (data) => {
  try {
    await cleanSourceOnlineFetcher.handler(data.id);
    await client.request(endpoints.submitBatch(data));
  } catch (exception) {
    throw createError('submitBatch', 'Failed to submit batch.', exception);
  }
});

/** GetDocumentLayout fetcher. */
export const getDocumentLayoutFetcher = createFetcher<GetDocumentLayoutRequestData, DocumentLayout | undefined>(
  endpoints.getDocumentLayout.key,
  async (requestData) => {
    try {
      const response = await client.request(endpoints.getDocumentLayout(requestData));
      if (response.status === 204) {
        return undefined;
      }

      return base64.decodeObj<DocumentLayout>(getData(response));
    } catch (exception) {
      throw createError('getDocumentLayout', 'Failed to get document layout.', exception);
    }
  },
);

/** GetOCRPage fetcher. */
export const getOCRPageFetcher = createFetcher<GetOCRPageRequestData, OCRPosition | undefined>(endpoints.getOCRPage.key, async (requestData) => {
  try {
    const response = await client.request(endpoints.getOCRPage(requestData));
    if (response.status === 204) {
      return undefined;
    }

    return getData<OCRPosition>(response);
  } catch (exception) {
    throw createError('getOCRPage', 'Failed to get ocr page.', exception);
  }
});

/** GetProfiles fetcher. */
export const getProfilesFetcher = createFetcher<string, ProfileSettings>(endpoints.getProfiles.key, async (batchTypeId) => {
  try {
    const { allowChanges, scannerName, scanner, separationName, separation, classification, batchOptions, documentOptions, isAIConfigured } =
      getData<GetProfilesResponse>(await client.request(endpoints.getProfiles(batchTypeId)));

    return {
      allowChanges,
      scanning: {
        name: scannerName,
        options: prepareScanningOptions(scanner),
      },
      separation: {
        name: separationName,
        separationOptions: prepareSeparationOptions(separation),
        classificationOptions: prepareClassificationOptions(classification),
      },
      batchOptions: prepareBatchOptions(batchOptions),
      documentOptions: prepareDocumentOptions(documentOptions),
      isAIConfigured: isAIConfigured ?? false,
    };
  } catch (exception) {
    throw createError('getProfiles', 'Failed to get profiles.', exception);
  }
});

/** SaveProfiles fetcher. */
export const saveProfilesFetcher = createFetcher<SaveProfilesRequestData>(endpoints.saveProfiles.key, async (requestData) => {
  try {
    await client.request(endpoints.saveProfiles(requestData));
  } catch (exception) {
    throw createError('saveProfiles', 'Failed to save profiles.', exception);
  }
});

/** GetBatchDataSource fetcher. */
export const getBatchDataSourceFetcher = createFetcher<GetDataSourceRequestData, BatchFieldWithValue[]>(
  endpoints.getBatchDataSource.key,
  async (requestData) => {
    try {
      const data = getData<TransformFieldValues>(await client.request(endpoints.getBatchDataSource(requestData)));

      return requestData.batchFields.map((field) => ({
        ...field,
        value: data[field.id],
      }));
    } catch (exception) {
      throw createError('getBatchDataSource', 'Failed to get batch data source.', exception);
    }
  },
);

/** GetDocumentDataSource fetcher. */
export const getDocumentDataSourceFetcher = createFetcher<GetDataSourceRequestData, DocumentFieldWithValue[]>(
  endpoints.getDocumentDataSource.key,
  async (requestData) => {
    try {
      const data = getData<TransformFieldValues>(await client.request(endpoints.getDocumentDataSource(requestData)));

      return (
        requestData.documentFields?.map((field) => ({
          ...field,
          value: data[field.id],
        })) ?? []
      );
    } catch (exception) {
      throw createError('getDocumentDataSource', 'Failed to get document data source.', exception);
    }
  },
);

/** GetBatchFieldDataSource fetcher. */
export const getBatchFieldDataSourceFetcher = createFetcher<GetBatchFieldDataSourceRequestData, GetBatchFieldsDataSourceResponse>(
  endpoints.getBatchFieldDataSource.key,
  async (requestData) => {
    try {
      return getData<GetBatchFieldsDataSourceResponse>(await client.request(endpoints.getBatchFieldDataSource(requestData)));
    } catch (exception) {
      throw createError('getBatchFieldDataSource', 'Failed to get data source on batch field changed.', exception);
    }
  },
);

/** GetDocumentFieldDataSource fetcher. */
export const getDocumentFieldDataSourceFetcher = createFetcher<GetDocumentFieldDataSourceRequestData, GetDocumentFieldsDataSourceResponse>(
  endpoints.getDocumentFieldDataSource.key,
  async (requestData) => {
    try {
      return getData<GetDocumentFieldsDataSourceResponse>(await client.request(endpoints.getDocumentFieldDataSource(requestData)));
    } catch (exception) {
      throw createError('getDocumentFieldDataSource', 'Failed to get data source on document field changed.', exception);
    }
  },
);

/** ValidateFields fetcher. */
export const validateFieldsFetcher = createFetcher<ValidateFieldsRequestData, ValidateFieldsResponse>(endpoints.validateFields.key, async (requestData) => {
  try {
    return getData<ValidateFieldsResponse>(await client.request(endpoints.validateFields(requestData)));
  } catch (exception) {
    throw createError('validateFields', 'Failed to validate fields.', exception);
  }
});

/** ExtendLockBatch fetcher. */
export const extendLockBatchFetcher = createFetcher<string, void>(endpoints.extendLockBatch.key, async (requestData) => {
  try {
    return await client.request(endpoints.extendLockBatch(requestData));
  } catch (exception) {
    throw createError('extendLockBatch', 'Failed to lock batch.', exception);
  }
});

/** UnlockBatch fetcher. */
export const unlockBatchFetcher = createFetcher<string, void>(endpoints.unlockBatch.key, async (requestData) => {
  try {
    return await client.request(endpoints.unlockBatch(requestData));
  } catch (exception) {
    throw createError('unlockBatch', 'Failed to unlock batch.', exception);
  }
});
